import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import huitziService from '../../services/huitziService';

/**
 * HuitziChat — Códice Botánico chat panel.
 *
 * A botanical-apothecary chat surface: deep jade-night parchment, a fret
 * (greca) crest, the colibrí messenger, and message turns rendered like
 * codex entries. Behaviour is unchanged from the original (history in
 * localStorage, Enter-to-send, suggested-action chips, typing indicator).
 *
 * Props, service calls, and the input autofill defenses are preserved.
 */
const HuitziChat = ({ open, onClose, isAuthenticated, onAction }) => {
  const [messages, setMessages] = useState(() => huitziService.getHistory());
  const [draft, setDraft] = useState('');
  const [typing, setTyping] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, typing, open]);

  useEffect(() => {
    if (open && messages.length === 0) {
      const greeting = isAuthenticated
        ? '¡Hola! Soy Huitzi 🐦. ¿En qué te ayudo? Puedo proponerte un plan semanal, contarte sobre una planta o sugerirte recetas.'
        : '¡Hola! Soy Huitzi 🐦, mensajero de la salud. Pregúntame sobre plantas medicinales mexicanas, combinaciones de alimentos o cómo cocinar bien las verduras.';
      const entry = huitziService.appendMessage('assistant', greeting);
      setMessages((prev) => [...prev, entry]);
    }
  }, [open, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || typing) return;
    setDraft('');
    const userEntry = huitziService.appendMessage('user', text);
    setMessages((prev) => [...prev, userEntry]);
    setTyping(true);

    const response = await huitziService.sendMessage(text, { isAuthenticated });
    const assistantEntry = huitziService.appendMessage(
      'assistant',
      response.reply,
      { actions: response.suggested_actions || [] },
    );
    setMessages((prev) => [...prev, assistantEntry]);
    setTyping(false);
  };

  const handleAction = (action) => {
    if (typeof onAction === 'function') {
      onAction(action);
    }
    const echo = huitziService.appendMessage('user', `(acción) ${action.label || action.intent}`);
    setMessages((prev) => [...prev, echo]);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearHistory = () => {
    huitziService.clearHistory();
    setMessages([]);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 28 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 24 }}
          transition={{ type: 'spring', stiffness: 380, damping: 30 }}
          style={styles.panel}
          className="cdx-grain cdx-scroll-night"
        >
          {/* Fret crest */}
          <div className="cdx-greca--gold cdx-greca" style={styles.crest} />

          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerLeft}>
              <Colibri />
              <div>
                <div style={styles.title}>Huitzi</div>
                <div style={styles.subtitle}>
                  {isAuthenticated ? 'mensajero · sesión iniciada' : 'mensajero de la salud'}
                </div>
              </div>
            </div>
            <div style={styles.headerActions}>
              <button
                type="button"
                onClick={clearHistory}
                style={styles.iconBtn}
                title="Limpiar conversación"
                aria-label="Limpiar conversación"
              >
                ⟲
              </button>
              <button
                type="button"
                onClick={onClose}
                style={styles.iconBtn}
                title="Cerrar"
                aria-label="Cerrar chat"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollerRef} style={styles.scroller} className="cdx-scroll-night">
            {messages.map((m, i) => (
              <MessageRow key={i} message={m} onAction={handleAction} />
            ))}
            {typing && <TypingRow />}
          </div>

          {/* Composer */}
          <div style={styles.composer}>
            <input
              type="text"
              name="huitzi-prompt"
              placeholder={isAuthenticated ? 'Escribe a Huitzi…' : 'Pregúntale a Huitzi…'}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKey}
              style={styles.input}
              disabled={typing}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="sentences"
              spellCheck="false"
              data-form-type="other"
              data-lpignore="true"
              data-1p-ignore="true"
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={typing || draft.trim().length === 0}
              style={{
                ...styles.sendBtn,
                opacity: typing || draft.trim().length === 0 ? 0.45 : 1,
                cursor: typing || draft.trim().length === 0 ? 'default' : 'pointer',
              }}
              aria-label="Enviar mensaje"
            >
              <SendGlyph />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* --------------------------------------------------------------------------
   Message row — codex entry styling, role-aware
   -------------------------------------------------------------------------- */
const MessageRow = ({ message, onAction }) => {
  const isUser = message.role === 'user';
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      style={{ ...styles.row, justifyContent: isUser ? 'flex-end' : 'flex-start' }}
    >
      {!isUser && <span style={styles.glyphMini} aria-hidden>🌿</span>}
      <div style={isUser ? styles.bubbleUser : styles.bubbleBot}>
        <p style={styles.bubbleText}>{message.content}</p>
        {Array.isArray(message.actions) && message.actions.length > 0 && (
          <div style={styles.actions}>
            {message.actions.map((a, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => onAction(a)}
                style={styles.actionChip}
              >
                {a.label || a.intent}
              </button>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const TypingRow = () => (
  <div style={{ ...styles.row, justifyContent: 'flex-start' }}>
    <span style={styles.glyphMini} aria-hidden>🌿</span>
    <div style={styles.bubbleBot}>
      <div style={styles.typing}>
        <span style={{ ...styles.dot, animationDelay: '0s' }} />
        <span style={{ ...styles.dot, animationDelay: '0.18s' }} />
        <span style={{ ...styles.dot, animationDelay: '0.36s' }} />
      </div>
    </div>
  </div>
);

/* --------------------------------------------------------------------------
   Colibrí line-art crest (small, header)
   -------------------------------------------------------------------------- */
const Colibri = () => (
  <div style={styles.colibriWrap} aria-hidden>
    <svg width="38" height="38" viewBox="0 0 48 48" fill="none">
      <circle cx="24" cy="24" r="23" fill="#1E2C24" stroke="#C9A227" strokeWidth="1.2" />
      {/* body */}
      <path d="M19 30c-1-5 1-9 5-11 3-1.5 7-1 9 1l-3 2 4 0-2 3 3 1c-2 2-6 3-9 2" fill="#2E8B57" />
      {/* head + beak */}
      <circle cx="18.5" cy="20" r="3.4" fill="#1F5C3D" />
      <path d="M15.5 19l-7-3 7 1.6z" fill="#C9A227" />
      <circle cx="17.6" cy="19.2" r="0.9" fill="#EBE2CF" />
      {/* wing — flutters */}
      <g style={{ transformOrigin: '22px 22px', animation: 'cdx-flutter 0.45s ease-in-out infinite' }}>
        <path d="M22 22c4-4 9-5 13-3-3 1-5 3-6 6-3 0-6-1-7-3z" fill="#E0892E" opacity="0.92" />
      </g>
      {/* tail */}
      <path d="M30 31l8 3-8 0z" fill="#8E2C42" />
    </svg>
  </div>
);

const SendGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M3 11.5 20 4l-7.5 17-2.7-6.8L3 11.5Z" fill="#16201A" stroke="#16201A"
      strokeWidth="1.2" strokeLinejoin="round" />
  </svg>
);

const styles = {
  panel: {
    position: 'absolute',
    bottom: '100%',
    right: 0,
    marginBottom: 18,
    width: 380,
    maxWidth: '92vw',
    height: 520,
    maxHeight: '74vh',
    background:
      'radial-gradient(120% 80% at 100% 0%, #1E2C24 0%, #16201A 55%, #111912 100%)',
    borderRadius: 'var(--cdx-r-xl)',
    border: '1px solid var(--cdx-night-edge)',
    boxShadow: 'var(--cdx-shadow-lg)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    fontFamily: 'var(--cdx-font-body)',
  },
  crest: { position: 'relative', zIndex: 2 },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 16px 12px',
    borderBottom: '1px solid var(--cdx-night-edge)',
    position: 'relative',
    zIndex: 2,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  colibriWrap: { animation: 'cdx-bob 3.4s ease-in-out infinite' },
  title: {
    fontFamily: 'var(--cdx-font-display)',
    fontWeight: 600,
    fontSize: 19,
    color: 'var(--cdx-night-text)',
    lineHeight: 1.1,
    letterSpacing: '-0.01em',
  },
  subtitle: {
    fontFamily: 'var(--cdx-font-mono)',
    fontSize: 10.5,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--cdx-gold)',
    marginTop: 2,
  },
  headerActions: { display: 'flex', gap: 6 },
  iconBtn: {
    width: 30,
    height: 30,
    borderRadius: 8,
    border: '1px solid var(--cdx-night-edge)',
    background: 'rgba(255,255,255,0.02)',
    color: 'var(--cdx-night-faint)',
    fontSize: 14,
    cursor: 'pointer',
    transition: 'all 0.18s',
  },
  scroller: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    position: 'relative',
    zIndex: 2,
  },
  row: { display: 'flex', alignItems: 'flex-end', gap: 8 },
  glyphMini: { fontSize: 16, marginBottom: 2, filter: 'saturate(1.2)' },
  bubbleBot: {
    maxWidth: '80%',
    background: 'var(--cdx-night-2)',
    border: '1px solid var(--cdx-night-edge)',
    borderLeft: '3px solid var(--cdx-jade-bright)',
    borderRadius: '4px 14px 14px 14px',
    padding: '10px 13px',
  },
  bubbleUser: {
    maxWidth: '80%',
    background: 'linear-gradient(160deg, #E0892E, #C26F1C)',
    borderRadius: '14px 4px 14px 14px',
    padding: '10px 13px',
    boxShadow: '0 2px 8px rgba(184,83,31,0.3)',
  },
  bubbleText: {
    margin: 0,
    fontSize: 14,
    lineHeight: 1.55,
    color: 'var(--cdx-night-text)',
    whiteSpace: 'pre-wrap',
  },
  actions: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 },
  actionChip: {
    fontFamily: 'var(--cdx-font-mono)',
    fontSize: 11,
    letterSpacing: '0.02em',
    color: 'var(--cdx-night-text)',
    background: 'rgba(46,139,87,0.16)',
    border: '1px solid var(--cdx-jade-bright)',
    borderRadius: 999,
    padding: '5px 11px',
    cursor: 'pointer',
    transition: 'all 0.18s',
  },
  typing: { display: 'flex', gap: 5, padding: '3px 2px' },
  dot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: 'var(--cdx-jade-bright)',
    animation: 'huitzi-typing 1.1s ease-in-out infinite',
  },
  composer: {
    display: 'flex',
    gap: 8,
    padding: 12,
    borderTop: '1px solid var(--cdx-night-edge)',
    background: 'rgba(0,0,0,0.18)',
    position: 'relative',
    zIndex: 2,
  },
  input: {
    flex: 1,
    background: 'var(--cdx-night)',
    border: '1px solid var(--cdx-night-edge)',
    borderRadius: 10,
    padding: '11px 14px',
    fontSize: 14,
    fontFamily: 'var(--cdx-font-body)',
    color: 'var(--cdx-night-text)',
    outline: 'none',
  },
  sendBtn: {
    width: 44,
    border: 'none',
    borderRadius: 10,
    background: 'linear-gradient(160deg, #C9A227, #E0892E)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.18s',
  },
};

export default HuitziChat;
